import { useMemo } from 'react';

import { BaseFields } from '../fields/types';
import { BaseValidationError } from '../types';

import { $internals, FormActionsMap, FormAsyncActionsMap, FormDeclaration } from './types';
import { useSubscription } from './useSubscription';

/** Параметры хука `useFormContext` */
type UseFormContextOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
> = {
    /** Форма, состояние и поля которой нужно получить */
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        FormAsyncActions
    >;
    /** Идентификатор формы */
    id: string;
};

/** Возвращает значение контекста формы */
export function useFormContext<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
>({
    form,
    id,
}: UseFormContextOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions
>) {
    const formInstance = useMemo(() => form.getInstance(id), [form, id]);
    useSubscription(formInstance);

    return formInstance[$internals].useContext();
}
